import { MapaDeChuvaFiltroIndice } from "../typings/mapaDeChuva";
import { REFERENCE_RAINFALL_BANDS } from "./constants";
import { RainfallBand } from "./types";

const RANGE_RE =
  /(-?\d+(?:[.,]\d+)?)\s*(?:-|–|—|a|até)\s*(-?\d+(?:[.,]\d+)?)/i;
const LTE_RE = /(?:≤|<=|≦)\s*(-?\d+(?:[.,]\d+)?)/i;
const GTE_RE = /(?:≥|>=|≧)\s*(-?\d+(?:[.,]\d+)?)/i;

const GT_PREFIX_RE = /(?:>|maior(?:\s+que)?)\s*(-?\d+(?:[.,]\d+)?)/i;

const GT_SUFFIX_RE = /(-?\d+(?:[.,]\d+)?)\s*>/;
const LT_RE = /(?:<|menor(?:\s+que)?)\s*(-?\d+(?:[.,]\d+)?)/i;
const ONLY_ZERO_RE = /^\s*0(?:[.,]0+)?\s*(?:mm)?\s*$/i;

function parseMm(raw: string): number {
  return Number(raw.replace(",", "."));
}

export function parseBandFromDescription(
  descricao: string,
  codigo: number,
  color: string
): RainfallBand {
  const label = descricao?.trim() || `${codigo}`;
  const text = label.toLowerCase();

  if (ONLY_ZERO_RE.test(label) || /igual\s+a\s+0/.test(text)) {
    return {
      codigo,
      minMm: Number.NEGATIVE_INFINITY,
      maxMm: 0,
      color,
      label,
    };
  }

  const lte = label.match(LTE_RE);
  if (lte) {
    return {
      codigo,
      minMm: Number.NEGATIVE_INFINITY,
      maxMm: parseMm(lte[1]),
      color,
      label,
    };
  }

  const lt = label.match(LT_RE);
  if (lt) {
    return {
      codigo,
      minMm: Number.NEGATIVE_INFINITY,
      maxMm: parseMm(lt[1]),
      color,
      label,
    };
  }

  const gtSuffix = label.match(GT_SUFFIX_RE);
  if (gtSuffix) {
    return {
      codigo,
      minMm: parseMm(gtSuffix[1]),
      maxMm: Number.POSITIVE_INFINITY,
      color,
      label,
    };
  }

  const gtPrefix = label.match(GT_PREFIX_RE);
  if (gtPrefix) {
    return {
      codigo,
      minMm: parseMm(gtPrefix[1]),
      maxMm: Number.POSITIVE_INFINITY,
      color,
      label,
    };
  }

  const gte = label.match(GTE_RE);
  if (gte) {
    return {
      codigo,
      minMm: parseMm(gte[1]),
      maxMm: Number.POSITIVE_INFINITY,
      color,
      label,
    };
  }

  const range = label.match(RANGE_RE);
  if (range) {
    const a = parseMm(range[1]);
    const b = parseMm(range[2]);
    return {
      codigo,
      minMm: Math.min(a, b),
      maxMm: Math.max(a, b),
      color,
      label,
    };
  }

  return {
    codigo,
    minMm: Number.NEGATIVE_INFINITY,
    maxMm: Number.POSITIVE_INFINITY,
    color,
    label,
  };
}

export function resolveRainfallBands(
  indices: MapaDeChuvaFiltroIndice[] | null | undefined
): RainfallBand[] {
  if (!indices?.length) {
    return REFERENCE_RAINFALL_BANDS.filter(
      (band) => !isExcludedRainfallLegendDescription(band.label)
    );
  }

  return filterVisibleRainfallLegendIndices(indices).map((item) =>
    parseBandFromDescription(
      item.descricao,
      item.indicePluviometrico,
      item.cor
    )
  );
}

export function isExcludedRainfallLegendDescription(
  descricao: string | null | undefined
): boolean {
  const text = (descricao ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");


  return /^(?:<|≤|<=)\s*1(?:\s*mm)?$/.test(text);
}

export function filterVisibleRainfallLegendIndices(
  indices: MapaDeChuvaFiltroIndice[] | null | undefined
): MapaDeChuvaFiltroIndice[] {
  if (!indices?.length) {
    return [];
  }
  return indices.filter(
    (item) => !isExcludedRainfallLegendDescription(item.descricao)
  );
}

export function findBandForValue(
  valueMm: number,
  bands: RainfallBand[]
): RainfallBand | null {
  if (!Number.isFinite(valueMm) || !bands.length) {
    return null;
  }

  for (const band of bands) {
    if (isValueInBand(valueMm, band)) {
      return band;
    }
  }

  return null;
}

export function isValueInBand(valueMm: number, band: RainfallBand): boolean {
  if (
    band.minMm === Number.NEGATIVE_INFINITY &&
    band.maxMm === Number.POSITIVE_INFINITY
  ) {
    return true;
  }

  if (band.minMm === Number.NEGATIVE_INFINITY) {
    return valueMm <= band.maxMm;
  }


  if (band.maxMm === Number.POSITIVE_INFINITY) {
    return valueMm > band.minMm;
  }


  return valueMm >= band.minMm && valueMm <= band.maxMm;
}

export function isBandSelected(
  band: RainfallBand,
  selectedBandCodes: number[] | null | undefined
): boolean {
  if (!selectedBandCodes?.length) {
    return false;
  }
  return selectedBandCodes.some(
    (code) => Number(code) === Number(band.codigo)
  );
}
